/**
 * Format time duration in a human-readable way
 * @param {number} hours - Time duration in hours
 * @returns {string} Human-readable time format
 */
export const formatTimeDuration = (hours) => {
  if (!hours || hours === 0) return '0 minutes';
  
  const totalMinutes = hours * 60;
  const minutesInHour = 60;
  const hoursInDay = 24;
  const daysInWeek = 7;
  const daysInMonth = 30.44; // Average days in month
  const daysInYear = 365.25; // Account for leap years
  
  const minutes = totalMinutes;
  const hoursNum = minutes / minutesInHour;
  const days = hoursNum / hoursInDay;
  const weeks = days / daysInWeek;
  const months = days / daysInMonth;
  const years = days / daysInYear;
  
  // Round to 2 decimal places for display
  const roundToTwo = (num) => Math.round(num * 100) / 100;
  
  if (years >= 1) {
    const yearsRounded = roundToTwo(years);
    const remainingMonths = roundToTwo((years - Math.floor(years)) * 12);
    
    if (remainingMonths >= 1) {
      return `${Math.floor(years)}y ${remainingMonths}mo`;
    }
    return yearsRounded === 1 ? '1 year' : `${yearsRounded} years`;
  }
  
  if (months >= 1) {
    const monthsRounded = roundToTwo(months);
    const remainingWeeks = roundToTwo((months - Math.floor(months)) * daysInMonth / daysInWeek);
    
    if (remainingWeeks >= 1) {
      return `${Math.floor(months)}mo ${remainingWeeks}w`;
    }
    return monthsRounded === 1 ? '1 month' : `${monthsRounded} months`;
  }
  
  if (weeks >= 1) {
    const weeksRounded = roundToTwo(weeks);
    const remainingDays = roundToTwo((weeks - Math.floor(weeks)) * daysInWeek);
    
    if (remainingDays >= 1) {
      return `${Math.floor(weeks)}w ${remainingDays}d`;
    }
    return weeksRounded === 1 ? '1 week' : `${weeksRounded} weeks`;
  }
  
  if (days >= 1) {
    const daysRounded = roundToTwo(days);
    const remainingHours = roundToTwo((days - Math.floor(days)) * hoursInDay);
    
    if (remainingHours >= 1) {
      return `${Math.floor(days)}d ${remainingHours}h`;
    }
    return daysRounded === 1 ? '1 day' : `${daysRounded} days`;
  }
  
  if (hoursNum >= 1) {
    const hoursRounded = roundToTwo(hoursNum);
    const remainingMinutes = roundToTwo((hoursNum - Math.floor(hoursNum)) * minutesInHour);
    
    if (remainingMinutes >= 1) {
      return `${Math.floor(hoursRounded)}h ${remainingMinutes}m`;
    }
    return hoursRounded === 1 ? '1 hour' : `${hoursRounded} hours`;
  }
  
  // Less than 1 hour, show in minutes
  const minutesRounded = roundToTwo(minutes);
  if (minutesRounded === 1) {
    return '1 minute';
  }
  return `${minutesRounded} minutes`;
};

/**
 * Format time in a compact way for small spaces
 * @param {number} hours - Time duration in hours  
 * @returns {string} Compact time format
 */
export const formatTimeCompact = (hours) => {
  if (!hours || hours === 0) return '0m';
  
  const totalMinutes = hours * 60;
  const minutesInHour = 60;
  const hoursInDay = 24;
  const daysInWeek = 7;
  const daysInMonth = 30.44;
  const daysInYear = 365.25;
  
  const minutes = totalMinutes;
  const hoursNum = minutes / minutesInHour;
  const days = hoursNum / hoursInDay;
  const weeks = days / daysInWeek;
  const months = days / daysInMonth;
  const years = days / daysInYear;
  
  const roundToTwo = (num) => Math.round(num * 100) / 100;
  
  if (years >= 1) return `${roundToTwo(years)}y`;
  if (months >= 1) return `${roundToTwo(months)}mo`;
  if (weeks >= 1) return `${roundToTwo(weeks)}w`;
  if (days >= 1) return `${roundToTwo(days)}d`;
  if (hoursNum >= 1) return `${roundToTwo(hoursNum)}h`;
  return `${roundToTwo(minutes)}m`;
};

/**
 * Format a date string in a human-readable datetime format
 * @param {string} dateString - ISO date string
 * @returns {string} Formatted datetime string
 */
export const formatDateTime = (dateString) => {
  if (!dateString) return '';
  
  const date = new Date(dateString);
  if (isNaN(date.getTime())) return '';
  
  return date.toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit'
  });
};

/**
 * Get the largest time unit for a given duration
 * @param {number} hours - Time duration in hours
 * @returns {string} The largest appropriate time unit
 */
export const getLargestTimeUnit = (hours) => {
  if (!hours || hours === 0) return 'minutes';
  
  const days = hours / 24;
  const weeks = days / 7;
  const months = days / 30.44;
  const years = days / 365.25;
  
  if (years >= 1) return years >= 2 ? 'years' : 'year';
  if (months >= 1) return months >= 2 ? 'months' : 'month';
  if (weeks >= 1) return weeks >= 2 ? 'weeks' : 'week';
  if (days >= 1) return days >= 2 ? 'days' : 'day';
  if (hours >= 1) return hours >= 2 ? 'hours' : 'hour';
  return minutes => minutes >= 2 ? 'minutes' : 'minute';
};